import React, { useState, useEffect } from "react";
import UpgradePlanModal from "@/components/UpgradePlanModal";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, MoreHorizontal, Key, UserX, CheckCircle, Search, Mail, RefreshCw, Copy, Link, Share2, Crown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ASSIGNABLE_ROLES, can } from "@/lib/permissions";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ASSIGNABLE_ROLES;

const roleColors = {
  super_admin: "bg-purple-100 text-purple-700",
  company_admin: "bg-blue-100 text-blue-700",
  project_manager: "bg-amber-100 text-amber-700",
  procurement: "bg-cyan-100 text-cyan-700",
  accountant: "bg-indigo-100 text-indigo-700",
  sales_person: "bg-orange-100 text-orange-700",
  field_engineer: "bg-emerald-100 text-emerald-700",
  management_view: "bg-slate-100 text-slate-600",
  client: "bg-pink-100 text-pink-700",
};

export default function TeamPage() {
  const { user, workspace, refreshWorkspace } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [tempCreds, setTempCreds] = useState(null);
  const [upgradeLimitMsg, setUpgradeLimitMsg] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ email: "", full_name: "", role: "field_engineer", phone: "" });

  const canManage = can(user, "users.manage");
  const isSuper = user?.role === "super_admin";
  const ownerId = workspace?.owner_id;
  const isOwner = isSuper || (ownerId && ownerId === user?.id);
  // Privilege ladder (mirrors backend): super_admin > owner > other admins > everyone else.
  // A non-owner admin cannot change role / status / password of another admin or of the owner.
  const canAdminister = (u) => canManage && u.id !== user.id && (isOwner || (u.role !== "company_admin" && u.id !== ownerId));
  const canChangeRole = (u) => canAdminister(u) && u.id !== ownerId;

  const transferOwnership = async (u) => {
    if (!window.confirm(`Make ${u.full_name} the workspace owner? You will remain an admin but lose owner-only controls.`)) return;
    try {
      await axios.post(`${API}/workspaces/transfer-ownership`, { new_owner_id: u.id });
      toast.success(`${u.full_name} is now the workspace owner`);
      refreshWorkspace && (await refreshWorkspace());
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Transfer failed"); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/users`);
      setUsers(data.filter(u => u.role !== "super_admin"));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const inviteUser = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await axios.post(`${API}/users/invite`, form);
      setUsers(p => [...p, data]);
      setTempCreds({ name: data.full_name, email: data.email, password: data.temp_password });
      setShowInvite(false);
      setForm({ email: "", full_name: "", role: "field_engineer", phone: "" });
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 403 && detail.toLowerCase().includes("limit")) {
        setShowInvite(false);
        setUpgradeLimitMsg(detail);
      } else {
        toast.error(detail || "Failed to create user");
      }
    }
    setInviting(false);
  };

  const resetPassword = async (userId, name) => {
    try {
      const { data } = await axios.post(`${API}/users/${userId}/reset-password`);
      setTempCreds({ name, email: "", password: data.temp_password });
      toast.success("Password reset");
    } catch (err) { console.error(err); }
  };

  const deactivateUser = async (userId) => {
    if (!window.confirm("Deactivate this user? They won't be able to log in.")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      setUsers(p => p.map(u => u.id === userId ? { ...u, is_active: false } : u));
      toast.success("User deactivated");
    } catch (err) { console.error(err); }
  };

  const reactivateUser = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/reactivate`);
      setUsers(p => p.map(u => u.id === userId ? { ...u, is_active: true } : u));
      toast.success("User reactivated");
    } catch (err) { console.error(err); }
  };

  const resendInvite = async (userId, name) => {
    try {
      const { data } = await axios.post(`${API}/users/${userId}/resend-invite`);
      setTempCreds({ name, email: "", password: data.temp_password });
      toast.success("New credentials generated");
    } catch (err) { console.error(err); }
  };

  const updateRole = async (userId, role) => {
    try {
      await axios.put(`${API}/users/${userId}`, { role });
      setUsers(p => p.map(u => u.id === userId ? { ...u, role } : u));
      toast.success("Role updated");
    } catch (err) { console.error(err); }
  };

  const activeCount = users.filter(u => u.is_active).length;
  const inactiveCount = users.filter(u => !u.is_active).length;
  const signedInCount = users.filter(u => u.last_login).length;
  const neverSignedIn = users.filter(u => !u.last_login && u.is_active).length;

  const relTime = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const filteredUsers = users.filter(u =>
    !search ||
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.role?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="p-8 text-[#64748B]">Loading team...</div>;

  return (
    <div className="p-6 animate-fade-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#0A1128]">Team</h1>
          <p className="text-sm text-[#64748B]">{activeCount} active · {inactiveCount} inactive · {signedInCount}/{users.length} signed in</p>
        </div>
        {canManage && (
          <button onClick={() => setShowInvite(true)} className="flex items-center gap-2 bg-[#2F7D52] text-white font-semibold px-4 py-2 rounded-xl text-sm hover:bg-[#266B44] transition-colors active:scale-[0.98]" data-testid="invite-team-btn">
            <Plus size={14} />Invite Member
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-2.5 text-[#94A3B8]" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, or role..."
          className="w-full max-w-md border border-[#E2E8F0] rounded pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52]"
          data-testid="team-search"
        />
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">Name</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider hidden md:table-cell">Email</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">Role</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider hidden sm:table-cell">Status</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider hidden lg:table-cell">Last active</th>
                {canManage && <th className="px-3 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u.id} className={`border-b border-[#F1F5F9] hover:bg-[#F8FAFC] ${!u.is_active ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs ${u.is_active ? "bg-[#FEF3C7] text-[#0A1128]" : "bg-slate-100 text-slate-400"}`}>
                        {u.full_name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-[#0A1128] text-xs">{u.full_name}</div>
                        <div className="text-[10px] text-[#64748B] md:hidden">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs text-[#64748B] hidden md:table-cell">{u.email}</td>
                  <td className="px-3 py-3">
                    {canChangeRole(u) ? (
                      <select
                        value={u.role}
                        onChange={e => updateRole(u.id, e.target.value)}
                        className={`text-[10px] font-bold uppercase px-2 py-1 rounded border-0 cursor-pointer ${roleColors[u.role] || ""}`}
                      >
                        {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    ) : (
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${roleColors[u.role] || ""}`}>
                        {ROLES.find(r => r.value === u.role)?.label || u.role}
                      </span>
                    )}
                    {u.id === ownerId && (
                      <span className="ml-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#0A1128] text-white" title="Workspace owner" data-testid={`owner-badge-${u.id}`}>Owner</span>
                    )}
                  </td>
                  <td className="px-3 py-3 hidden sm:table-cell">
                    {!u.is_active ? (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-700">Inactive</span>
                    ) : !u.last_login ? (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" title="Invited but never signed in" data-testid={`user-status-invited-${u.id}`}>Invited</span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-[11px] text-[#64748B] hidden lg:table-cell" data-testid={`user-lastactive-${u.id}`}>
                    {u.last_login ? relTime(u.last_login) : <span className="text-amber-600">Never signed in</span>}
                  </td>
                  {canManage && (
                    <td className="px-3 py-3">
                      {canAdminister(u) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger className="p-1.5 rounded hover:bg-slate-50 text-[#64748B] hover:text-[#0A1128]" data-testid={`user-actions-${u.id}`}>
                            <MoreHorizontal size={14} />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => resetPassword(u.id, u.full_name)}>
                              <Key size={12} className="mr-2" />Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => resendInvite(u.id, u.full_name)}>
                              <Mail size={12} className="mr-2" />Resend Invite
                            </DropdownMenuItem>
                            {isOwner && u.role === "company_admin" && u.is_active && u.id !== ownerId && (
                              <DropdownMenuItem onClick={() => transferOwnership(u)}>
                                <Crown size={12} className="mr-2" />Make Owner
                              </DropdownMenuItem>
                            )}
                            {u.is_active ? (
                              <DropdownMenuItem onClick={() => deactivateUser(u.id)} className="text-red-600">
                                <UserX size={12} className="mr-2" />Deactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => reactivateUser(u.id)} className="text-emerald-600">
                                <RefreshCw size={12} className="mr-2" />Reactivate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-[#64748B] text-sm">
                    {search ? "No members match your search." : "No team members yet. Invite your first member."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Modal */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading font-bold text-[#0A1128]">Invite Team Member</DialogTitle></DialogHeader>
          <form onSubmit={inviteUser} className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Full Name *</label>
              <input type="text" required value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52]" data-testid="invite-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Email *</label>
              <input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52]" data-testid="invite-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Role</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52] bg-white" data-testid="invite-role">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <p className="text-xs text-amber-700">User will receive a temporary password shown after creation. Share it manually if email delivery is unavailable.</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 border border-[#E2E8F0] rounded text-sm text-[#64748B] hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={inviting} className="bg-[#FFB703] text-[#0A1128] font-semibold px-5 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors disabled:opacity-60" data-testid="invite-submit-btn">
                {inviting ? "Creating..." : "Create User"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Modal */}
      {tempCreds && (
        <Dialog open onOpenChange={() => setTempCreds(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-[#059669] flex items-center gap-2">
                <CheckCircle size={18} />User Created Successfully
              </DialogTitle>
            </DialogHeader>
            <div className="py-2 space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-800 mb-1">Important — Share these with {tempCreds.name}</p>
                <p className="text-xs text-amber-700">Ask them to open the login link and use the temporary password below. They should change it after first sign-in.</p>
              </div>

              {tempCreds.email && (
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Login Email</div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(tempCreds.email); toast.success("Email copied!"); }}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                      data-testid="copy-email-btn"
                    >
                      <Copy size={11} />Copy
                    </button>
                  </div>
                  <div className="font-mono text-sm font-semibold text-[#0A1128] break-all">{tempCreds.email}</div>
                </div>
              )}

              <div className="bg-[#FEF3C7] border border-[#FFB703]/40 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Temporary Password</div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(tempCreds.password); toast.success("Password copied!"); }}
                    className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-semibold"
                    data-testid="copy-password-btn"
                  >
                    <Copy size={11} />Copy
                  </button>
                </div>
                <div className="font-mono text-base font-bold text-[#0A1128] tracking-widest">{tempCreds.password}</div>
              </div>

              <div className="bg-[#F0FDF4] border border-emerald-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Login Link</div>
                  <button
                    onClick={() => {
                      const loginUrl = `${window.location.origin}/login${tempCreds.email ? `?email=${encodeURIComponent(tempCreds.email)}` : ""}`;
                      navigator.clipboard.writeText(loginUrl);
                      toast.success("Login link copied!");
                    }}
                    className="flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 font-medium"
                    data-testid="copy-login-link-btn"
                  >
                    <Link size={11} />Copy Link
                  </button>
                </div>
                <div className="text-xs text-emerald-800 font-medium truncate">{window.location.origin}/login</div>
              </div>

              <button
                onClick={() => {
                  const loginUrl = `${window.location.origin}/login${tempCreds.email ? `?email=${encodeURIComponent(tempCreds.email)}` : ""}`;
                  const msg = `Hi ${tempCreds.name},\n\nYou've been invited to SunBiz!\n\nLogin Email: ${tempCreds.email}\nTemporary Password: ${tempCreds.password}\n\nLogin here: ${loginUrl}\n\nPlease change your password after signing in.`;
                  navigator.clipboard.writeText(msg);
                  toast.success("Full invite message copied! Paste it in WhatsApp or email.");
                }}
                className="w-full flex items-center justify-center gap-2 border border-[#E2E8F0] text-[#374151] font-semibold py-2 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                data-testid="copy-full-message-btn"
              >
                <Share2 size={13} />Copy Full Invite Message (for WhatsApp / Email)
              </button>
            </div>
            <button
              onClick={() => setTempCreds(null)}
              className="w-full bg-[#FFB703] text-[#0A1128] font-semibold py-2.5 rounded-lg text-sm hover:bg-[#E8A200] transition-colors"
              data-testid="credentials-done-btn"
            >
              Done
            </button>
          </DialogContent>
        </Dialog>
      )}

      {upgradeLimitMsg && (
        <UpgradePlanModal message={upgradeLimitMsg} onClose={() => setUpgradeLimitMsg(null)} />
      )}
    </div>
  );
}
